import { useEffect, useState } from "react";

export interface Option {
  value: number | string;
  label: string;
}

/** Số ký tự tối thiểu mới đi tìm. Dưới ngưỡng này kết quả rộng tới mức vô nghĩa. */
const MIN_SEARCH_LENGTH = 2;
/** Chờ người dùng ngừng gõ rồi mới gọi API. */
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Bảng chọn loại đồ liên kết.
 *
 * Dựng thành lớp phủ `fixed` chứ không phải popover trong ô: popover cũ nằm bên trong vùng có
 * `overflow` của bảng nên bị cắt và nuốt luôn thao tác cuộn. Ở đây cũng không đổ sẵn toàn bộ
 * danh sách — thực tế có hàng trăm loại đồ, người dùng gõ để tìm rồi thêm từng cái.
 */
export const LinkedCategoryPicker: React.FC<{
  selectedIds: string[];
  labelOf: (id: string) => string;
  onSearch?: (keyword: string) => Promise<Option[]>;
  onToggle: (id: string, label: string) => void;
  onClose: () => void;
}> = ({ selectedIds, labelOf, onSearch, onToggle, onClose }) => {
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<Option[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const term = keyword.trim();

  useEffect(() => {
    if (term.length < MIN_SEARCH_LENGTH || !onSearch) {
      setResults([]);
      setSearching(false);
      setError(null);
      return;
    }

    // `cancelled` chặn kết quả của từ khoá cũ về sau ghi đè kết quả mới.
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      onSearch(term)
        .then((opts) => {
          if (cancelled) return;
          setResults(opts);
          setError(null);
        })
        .catch(() => {
          if (cancelled) return;
          setResults([]);
          setError("Không tìm được loại đồ. Kiểm tra kết nối rồi thử lại.");
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term, onSearch]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[70vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-base font-semibold text-slate-800">Loại đồ liên kết</div>
            <div className="mt-1 text-xs text-slate-500">
              Dùng để mồi kiểm đếm ở đơn đầu tiên và gợi ý số lượng. <b>Không bắt buộc.</b>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 shrink-0 rounded-full text-xl text-slate-400 hover:bg-slate-100"
          >
            ×
          </button>
        </div>

        <div className="border-b border-slate-200 px-5 py-3">
          <input
            autoFocus
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Nhập tên loại đồ để tìm…"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />

          {selectedIds.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedIds.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => onToggle(id, labelOf(id))}
                  title="Bỏ liên kết"
                  className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-300 hover:bg-emerald-200"
                >
                  {labelOf(id)} ×
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          {!onSearch ? (
            <div className="py-6 text-center text-sm text-slate-500">Màn hình này chưa nối tìm kiếm loại đồ.</div>
          ) : term.length < MIN_SEARCH_LENGTH ? (
            <div className="py-6 text-center text-sm text-slate-500">Nhập tối thiểu {MIN_SEARCH_LENGTH} ký tự để tìm.</div>
          ) : searching ? (
            <div className="py-6 text-center text-sm text-slate-500">Đang tìm…</div>
          ) : error ? (
            <div className="py-6 text-center text-sm text-rose-600">{error}</div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500">Không có loại đồ nào khớp “{term}”.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {results.map((opt) => {
                const id = String(opt.value);
                const selected = selectedIds.includes(id);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => onToggle(id, opt.label)}
                      className="flex w-full items-center justify-between gap-3 px-1 py-2 text-left text-sm hover:bg-slate-50"
                    >
                      <span className={selected ? "text-emerald-700" : "text-slate-700"}>{opt.label}</span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                          selected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {selected ? "Đã chọn" : "Thêm"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Xong
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkedCategoryPicker;
